import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faExclamationTriangle,
  faPlus,
  faTimesCircle,
} from '@fortawesome/free-solid-svg-icons';
import {
  FormContextProvider,
  FormSchemaFields,
} from '@stormwater-software/lib-form';
import { UIControlType } from '@stormwater-software/lib-form-control-types';
import { LayoutTypes } from '@stormwater-software/lib-inspection-templates';
import React, { useContext, useState } from 'react';
import { useQueryClient } from 'react-query';
import TemplateEditorContext from '../../../contexts/TemplateEditorContext';
import { pdfLayoutKey } from '../../../hooks/pdfLayout';
import { env } from '../../../config';
import {
  PdfLayout,
  PdfLayoutGroup,
  PdfLayoutResponse,
  PdfLayoutSection,
} from '../../../utils/api/pdfLayout';
import { inspectionTemplateLogoApi } from '../../../utils/api/inspectionTemplateLogo';
import { questionApi } from '../../../utils/api/question';
import { questionConfigApi } from '../../../utils/api/questionConfig';
import { defaultSection } from '../PdfLayout/DocumentPreview';
import HeaderFooterControls from '../PdfLayout/HeaderFooterControls';
import QuestionGroupForm from './Forms/QuestionGroupForm';
import QuestionGroups from './QuestionGroups';

interface Props {
  groups: PdfLayoutGroup[];
  layout: PdfLayout;
  sections: Record<number, PdfLayoutSection>;
  logoGuid: string | null;
  onPatchLayout: (fields: Partial<PdfLayout>) => void;
  onPatchSection: (groupId: number, fields: Partial<PdfLayoutSection>) => void;
}

const conditionSchema = {
  condition_group: {
    controlType: UIControlType.select,
    label: 'Condition Group',
    validation: { required: true },
    options: [
      { value: 'no', label: 'No Condition Group' },
      { value: 'apply', label: 'Apply Condition Group' },
    ],
  },
};

const CONDITION_OPTIONS = [
  '(I) Installation',
  '(M) BMP Maintenance',
  '(S) Street Sweeping',
  '(C) Contractor Responsibility',
  '(A) Additional Control Measure Needed',
  '(R) Remove Control Measure',
];

const StructurePanel: React.FC<Props> = ({
  groups,
  layout,
  sections,
  logoGuid,
  onPatchLayout,
  onPatchSection,
}) => {
  const templateEditorContext = useContext(TemplateEditorContext);
  const queryClient = useQueryClient();
  const [showGroupForm, setShowGroupForm] = useState(false);

  const sortedGroups = [...groups].sort((a, b) => a.order - b.order);
  const { templateId, warnings } = templateEditorContext;

  const setLogoGuid = (guid: string | null) =>
    queryClient.setQueryData<PdfLayoutResponse | undefined>(
      pdfLayoutKey(templateId),
      (prev) => prev && { ...prev, logo_guid: guid },
    );

  const uploadLogo = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const formData = new FormData();

    formData.append('file', event.target.files![0]);
    try {
      const guid = await inspectionTemplateLogoApi.post(templateId, formData);

      if (guid) setLogoGuid(guid);
    } catch {
      /* ignore upload failure */
    }
  };

  const deleteLogo = async () => {
    try {
      const res = await inspectionTemplateLogoApi.delete(templateId);

      if (res) setLogoGuid(null);
    } catch {
      /* ignore delete failure */
    }
  };

  const applyConditionGroup = async () => {
    const { id: groupId } = await templateEditorContext.addNewQuestionGroup({
      layout_type: LayoutTypes.FORM,
      name: 'Condition Group',
      order: templateEditorContext.getNextQuestionGroupOrder(),
    });

    const newQuestion = {
      features: [],
      groupId,
      input_label: 'Condition',
      options: CONDITION_OPTIONS,
      question_type_id: 2, // Select One
      sort_order: 0,
    };

    const { id: questionId } = await questionApi.create(groupId, newQuestion);

    // input_label is saved by questionApi.create; only options needs a config write.
    const optionsField = templateEditorContext.questionConfigData.find(
      (f: any) =>
        f.questionTypeId === newQuestion.question_type_id &&
        f.fieldName === 'options',
    );

    if (optionsField) {
      await questionConfigApi.update([
        {
          value: JSON.stringify(newQuestion.options),
          questionId,
          questionFieldId: optionsField.id,
        },
      ]);
    }

    await templateEditorContext.refetchQuestionGroups();
  };

  return (
    <aside className='utb-structure'>
      <div className='utb-structure__head'>
        <h4>Questions &amp; Structure</h4>
        <button
          type='button'
          className='utb-add-group'
          onClick={() => setShowGroupForm(true)}
        >
          <FontAwesomeIcon icon={faPlus} fontSize={10} /> &nbsp;Add Group
        </button>
      </div>

      <div className='utb-structure__toolbar'>
        <div className='utb-logo'>
          {logoGuid === null ? (
            <label htmlFor='add-logo' className='utb-logo__add'>
              Add Logo
              <input id='add-logo' type='file' onChange={uploadLogo} />
            </label>
          ) : (
            <span className='utb-logo__preview'>
              <img
                src={`${env.REACT_APP_API_BASE_URL}/dl/${logoGuid}`}
                alt='Template logo'
              />
              <FontAwesomeIcon icon={faTimesCircle} onClick={deleteLogo} />
            </span>
          )}
        </div>

        <div className='utb-condition-group'>
          <FormContextProvider>
            <FormSchemaFields
              schema={conditionSchema}
              initialFormData={{
                condition_group: templateEditorContext.isConditionGroupApplied
                  ? 'apply'
                  : 'no',
              }}
              formData={{
                condition_group: templateEditorContext.isConditionGroupApplied
                  ? 'apply'
                  : 'no',
              }}
              onChange={(key: string, value: string) => {
                if (
                  key === 'condition_group' &&
                  value === 'apply' &&
                  !templateEditorContext.isConditionGroupApplied
                ) {
                  applyConditionGroup();
                }
              }}
            />
          </FormContextProvider>
        </div>
      </div>

      {warnings.length > 0 && (
        <h5 className='error-messages'>
          {warnings.includes('type') && (
            <em>
              <FontAwesomeIcon icon={faExclamationTriangle} />
              Template must contain question with 'Inspection Type' feature
            </em>
          )}
          {warnings.includes('date') && (
            <em>
              <FontAwesomeIcon icon={faExclamationTriangle} />
              Template must contain question with 'Inspection Date' feature
            </em>
          )}
        </h5>
      )}

      <div className='utb-structure__groups'>
        <HeaderFooterControls layout={layout} onPatchLayout={onPatchLayout} />

        {sortedGroups.map((group, index) => (
          <QuestionGroups
            key={group.id}
            group={group}
            index={index}
            count={sortedGroups.length}
            section={sections[group.id] ?? defaultSection(group.id)}
            pageBreakAfter={!!sections[group.id]?.page_break_after}
            onChangeSection={(fields) => onPatchSection(group.id, fields)}
            onTogglePageBreak={(on) =>
              onPatchSection(group.id, { page_break_after: on })
            }
          />
        ))}
      </div>

      {showGroupForm && (
        <QuestionGroupForm
          handleClose={() => setShowGroupForm(false)}
          order={templateEditorContext.getNextQuestionGroupOrder()}
        />
      )}
    </aside>
  );
};

export default StructurePanel;
